import React, { useEffect, useState } from 'react';
import moment from 'moment';
import lodash from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import FeedLoading from './FeedLoading';
import CreateFeedItem from './CreateFeedItem/CreateFeedItem';
import FeedNoticeItem from './FeedItem/FeedNoticeItem';
import FeedPollItem from './FeedItem/FeedPollItem';
import FeedHighFiveItem from './FeedItem/FeedHighFiveItem';
import FeedEventItem from './FeedItem/FeedEventItem';
import FeedLearningItem from './FeedItem/FeedLearningItem';
import FeedGroupManagementItem from './FeedItem/FeedGroupManagementItem';
import FeedOnboardingItem from './FeedItem/FeedOnboardingItem';
import Event from '../../containers/Engage/components/EventItem';
import { fetchFeatureFlagsRequest } from '../../actionCreators/featureFlags';
import { fetchBannerRequest } from '../../actionCreators/banner';
import { logFirebaseUserProperty } from '../../firebase/analytics';
import { getOrgThemeRequest } from '../../actionCreators/orgTheme';
import {
  getFeedItemDetailsRequest,
  getFeedItemRequest,
  getFeedRequest,
  resetFeedPage,
  resetSingleFeedPage,
  postCommentRequest,
  voteOnPostRequest,
  toggleLikeRequest,
  toggleCommentLikeRequest,
  populatePostReportTypesRequest,
  resetPostRequestSuccess,
} from '../../actionCreators/feedActions';
import { sendUserPulseRequest } from '../../actionCreators/userProfile';
import { getPasswordMigrationStatus } from '../../actionCreators/account';
import { getGroupRequest, toggleJoinGroupRequest } from '../../actionCreators/groupActions';
import { addAlertToAlertsList } from '../../actionCreators/globalAlerts';
import { logFirebaseEvent } from '../../firebase/analytics';
import FeedItemTag from './FeedItem/components/FeedItemTag';
import FeedItemHeader from './FeedItem/components/FeedItemHeader';
import FeedItemFooter from './FeedItem/components/FeedItemFooter';
import FeedItemFooterControl from './FeedItem/components/FeedItemFooterControl';
import FeedGroupPrivacy from './FeedItem/components/FeedGroupPrivacy';
import DailyPulse from '../DailyPulse/DailyPulse';
import FeedGroupHeader from './FeedGroupHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import GroupItem from '../GroupItem/GroupItem';
import Banner from './Banner/Banner';
import IconPinned from '../../assets/images/iconPinned.svg';
import { getTasksRequest } from '../../actionCreators/employeeOnboarding';
import GridContainer from '../global/GridContainer/GridContainer';
import KnowledgebaseItem from '../Knowledgebase/KnowledgebaseItem';
import { ChallengePostCard } from '../../components';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { UserListModal } from '../../containers';

const TYPE_MAP = {
  notices: 'notice',
  polls: 'poll',
  events: 'event',
  highfives: 'highfive',
  groups: 'group',
  learning: 'learning',
  group_management: 'group_management',
  knowledgebase_items: 'knowledgebase_items',
  challenges: 'challenges',
};

const BACK_TEXT = {
  '/': 'Back to Feed',
  '/engage': 'Back to Events',
};

const FeedComponent = (props) => {
  const navigate = useNavigate();
  const params = useParams();

  const scrollBlock = React.createRef();
  const contentBlock = React.createRef();

  const [state, setState] = useState({
    scrollEnabled: true,
    showDailyPulse: false,
    reportSubmitted: false,
  });

  let [searchParams] = useSearchParams();
  const hasGroupFeedId = params?.groupId;
  const hasPostSingleId = params?.postId;
  const type = searchParams.get('type');
  const hasVerifyGroup = props.match?.params?.verifyGroup;
  let userId = null;

  useEffect(() => {
    const run = async () => {
      // on load reset redux state for any previous feeds loaded.
      if (hasPostSingleId) await props.resetSingleFeed();
      else await props.resetFeed();

      logFirebaseUserProperty();
      props.getEmployeeOnboardingTasks();
      props.fetchOrgTheme();
      props.fetchFeatureFlags();
      props.fetchBanner();
      // Get feed single post
      if (hasPostSingleId) {
        await props.getFeedItem({ postId: hasPostSingleId, type });
        logFirebaseEvent({
          event: 'events',
          action: 'detail',
        });
      } else {
        // Get group
        if (hasGroupFeedId) props.getGroup(hasGroupFeedId);

        // Get all feed
        await props.getFeed({ groupId: hasGroupFeedId });
        logFirebaseEvent({
          event: 'feed',
          action: 'load',
        });
      }
      props.populatePostReportTypes();
      _manageDailyPulse(props);
      props.getPasswordMigrationStatus();
    };
    run();
  }, [hasGroupFeedId]);

  const _manageDailyPulse = (props) => {
    if (!props.userData.id || props.userData.pulse_replied || state.showDailyPulse) return;
    userId = props.userData.id;

    const dailyPulseFromStorage = localStorage.getItem(`dailyPulse-${userId}`);
    const dailyPulseData = JSON.parse(dailyPulseFromStorage || '{}');
    const day = parseInt(moment().format('DDD'));
    const year = parseInt(moment().format('YYYY'));

    if (
      dailyPulseData &&
      dailyPulseData.hideUntil &&
      dailyPulseData.hideUntil.year >= year &&
      dailyPulseData.hideUntil.day >= day
    )
      return;

    setState({ ...state, showDailyPulse: true });

    if (!dailyPulseFromStorage) {
      const newDailyPulseItem = { lastSeen: null, notNowClicked: 0 };
      localStorage.setItem(`dailyPulse-${userId}`, JSON.stringify(newDailyPulseItem));
    }
  };

  const _onScroll = () => {
    let currentGroup = null;
    if (hasGroupFeedId) currentGroup = props.groupsDetailed[hasGroupFeedId];
    const isOnNotJoinedGroup = !!(hasGroupFeedId && currentGroup && !currentGroup.is_join);

    if (isOnNotJoinedGroup) return;

    const height = scrollBlock.current.offsetHeight;
    const scroll = scrollBlock.current.scrollTop;
    const contentHeight = contentBlock.current.offsetHeight;

    if (!props.loading && height + scroll >= contentHeight - 60) {
      props.getFeed({ groupId: hasGroupFeedId });
    }
  };

  const getDetails = (postableId, type) => {
    props.getFeedItemDetails({ type, id: postableId });
  };

  const postCommentEvent = (postId, postableId, postType, commentText) => {
    logFirebaseEvent({
      event: postType,
      action: 'add_comment',
    });
    props.postComment({ postType, comment: commentText, postId, postableId });
  };

  const voteOnPostEvent = (postableId, postType, pollOptionId) => {
    props.voteOnPost({ postType, pollOptionId, postableId });
  };

  const toggleLike = (postId, postableId, postType, newLikeState) => {
    logFirebaseEvent({
      event: postType,
      action: newLikeState ? 'like' : 'unlike',
    });
    props.toggleLike({ postId, postableId, postType, newLikeState });
  };

  const toggleCommentLike = (postId, postableId, postType, commentId, newLikeState) => {
    logFirebaseEvent({
      event: 'comments',
      action: 'like',
    });
    props.toggleCommentLike({ postId, postableId, postType, newLikeState, commentId });
  };

  const toggleHideDailyPulse = () => {
    setState({ ...state, showDailyPulse: false });
  };

  const onToggleCreateFeed = (isActive) => {
    if (isActive) scrollBlock.current.scroll({ top: hasGroupFeedId ? 190 : 0, behavior: 'smooth' });

    setState({ ...state, scrollEnabled: !isActive });
  };

  const handleBack = () => {
    navigate(-1);
  };

  const renderFeedItem = (item, fullFunctionality = true) => {
    if (!item) return null;
    if (item.postable_type === 'highfives' && !item.post.recipient_id) return null;
    const author = props.feed.authors[item.author_id] || {};
    const itemType = TYPE_MAP[item.postable_type];
    const isNewGroupItem = itemType === 'group' ? true : false;
    const isNewMemberItem = itemType === 'group_management' ? true : false;
    const isKnowledgebaseItem = itemType === 'knowledgebase_items' ? true : false;
    const isChallengeItem = itemType === 'challenges' ? true : false;
    const link = fullFunctionality ? `/feed/${item.postable_id}?type=${itemType}` : '#';
    const campaignContentId = item.campaign_content_id;
    const group = props.groups[item.group_id];
    const feedItemGroup = item.post;
    const shouldRenderHeader =
      itemType !== 'highfive' &&
      itemType !== 'learning' &&
      itemType !== 'group_management' &&
      !isNewGroupItem &&
      !campaignContentId;

    const shouldRenderFooter =
      itemType !== 'learning' &&
      itemType !== 'group_management' &&
      !isNewGroupItem &&
      !isKnowledgebaseItem &&
      !isChallengeItem;
    const shouldRenderFooterControl =
      itemType !== 'learning' &&
      itemType !== 'group_management' &&
      !isNewGroupItem &&
      !isKnowledgebaseItem &&
      !isChallengeItem;
    const myGroups = props.myGroups.map((item) => item.id);

    let FeedItemComponent = null;
    if (itemType === 'notice') FeedItemComponent = FeedNoticeItem;
    if (itemType === 'poll') {
      FeedItemComponent = () => (
        <FeedPollItem
          item={item}
          voteOnPost={(pollOptionId) => voteOnPostEvent(item.postable_id, itemType, pollOptionId)}
          disableVote={!fullFunctionality}
          currentUser={props.userData}
          addAlertToAlerts={props.addAlertToAlerts}
        />
      );
    }
    if (itemType === 'event') FeedItemComponent = () => <FeedEventItem item={item} link={link} currentUser={props.userData} addAlertToAlerts={props.addAlertToAlerts} />;

    if (itemType === 'highfive') {
      const recipient = props.feed.authors[item.post.recipient_id];

      if (!recipient) return null;

      FeedItemComponent = () => (
        <FeedHighFiveItem
          recipient={recipient}
          author={author}
          item={item}
          setReportSubmitted={() => setState({ ...state, reportSubmitted: true })}
          currentUser={props.userData}
        />
      ); // [DK] check if any props are missing here...
    }

    if (itemType === 'learning') {
      FeedItemComponent = FeedLearningItem;
    }

    if (itemType === 'group_management' && hasGroupFeedId) {
      FeedItemComponent = FeedGroupManagementItem;
    }

    if (isNewGroupItem) {
      FeedItemComponent = GroupItem;
    }
    if (isKnowledgebaseItem) {
      FeedItemComponent = () => <KnowledgebaseItem item={item.post} />;
    }
    if (isChallengeItem) {
      FeedItemComponent = ChallengePostCard;
    }

    if (!FeedItemComponent) return null;

    return (
      <div>
        {campaignContentId ? (
          <div className="flex flex-column">
            <p className="m-0 p-0 feed-item-header-text">{item.campaign_title}</p>
            <p className="p-0 mt-2">{item.campaign_content_title}</p>
          </div>
        ) : null}

        {isNewGroupItem ? (
          <FeedItemHeader
            setReportSubmitted={() => setState({ ...state, reportSubmitted: true })}
            createdAt={item.created_at}
            author={author}
            link={link}
            group={feedItemGroup}
            hasGroupFeedId={hasGroupFeedId}
            postId={item.id}
            item={item}
            groupId={feedItemGroup.id}
            isNewGroupItem={isNewGroupItem}
            isPinned={item.is_pinned}
          />
        ) : null}
        {isNewMemberItem && (
          <FeedItemComponent {...props} myGroups={myGroups} users={props.feed.authors} currentUser={props.userData} addAlertToAlerts={props.addAlertToAlerts} />
        )}
        <div
          className={`${!isKnowledgebaseItem && !isChallengeItem ? 'feed-item' : 'mb-3'} ${
            item.post && item.post.is_global ? 'feed-item--global' : ''
          }`}
          key={item.postable_id}
        >
          {!hasGroupFeedId && group ? (
            <FeedGroupPrivacy groupPrivacyTypeId={group.group_privacy_type_id} />
          ) : null}
          {shouldRenderHeader && (
            <FeedItemHeader
              setReportSubmitted={() => setState({ ...state, reportSubmitted: true })}
              history={props.history}
              createdAt={item.created_at}
              author={author}
              link={link}
              group={group}
              hasGroupFeedId={hasGroupFeedId}
              postId={item.id}
              item={item}
              groupId={item.group_id}
              hasPostSingleId={hasPostSingleId}
              isPinned={item.is_pinned}
            />
          )}

          {item.post.is_global ? (
            <div className="feed-item-tag-container">
              <FeedItemTag title={'Wellbeing'} />
            </div>
          ) : null}

          {isNewGroupItem ? (
            <GroupItem group={feedItemGroup} />
          ) : isKnowledgebaseItem ? (
            <KnowledgebaseItem item={item.post} test={123} />
          ) : isChallengeItem ? (
            <ChallengePostCard challengeItem={item.post} from={'feed'} />
          ) : !isNewMemberItem ? (
            <FeedItemComponent item={item} myGroups={myGroups} currentUser={props.userData} addAlertToAlerts={props.addAlertToAlerts}/>
          ) : null}

          {shouldRenderFooter && (
            <div className='d-flex m-0 p-0 align-items-center'>
              <FeedItemFooter
                isPostSingle={hasPostSingleId}
                itemType={itemType}
                postContainerId={item.id}
                likes={item.likes_counter}
                comments={item.comments_counter}
                link={link}
              />
            </div>
            
          )}

          {shouldRenderFooterControl && (
            <FeedItemFooterControl
              comments={item.comments}
              onComment={getDetails.bind(this, item.postable_id, itemType)}
              isPostSingle={hasPostSingleId}
              link={link}
              postComment={(commentText) =>
                postCommentEvent(item.id, item.postable_id, itemType, commentText)
              }
              userImageUrl={props.userImageUrl}
              isLiked={!!item.post.is_liked_by_me}
              onLike={toggleLike.bind(
                this,
                item.id,
                item.postable_id,
                itemType,
                !item.post.is_liked_by_me,
              )}
              onCommentLike={toggleCommentLike.bind(this, item.id, item.postable_id, itemType)}
              disableButtons={!fullFunctionality}
              isMandatoryGroup={group ? group.is_mandatory : false}
            />
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    // Render single Post
    if (hasPostSingleId) {
      const { singleItem } = props.feed;
      if (!singleItem) return <FeedLoading isLoading amount={1} />;

      if (singleItem.postable_type === 'events') {
        const author = props.feed.authors[singleItem.author_id] || {};
        const group = props.groups[singleItem.group_id] || {};

        return (
          <Event
            history={props.history}
            eventItem={singleItem}
            postableId={singleItem.id}
            postId={singleItem.postable_id}
            author={author}
            group={group}
          />
        );
      }

      return renderFeedItem(singleItem, true);
    }

    const { items } = props.feed;

    if (items && items.length > 0) {
      // Decide if we need to show create post block
      let currentGroup = null;
      if (hasGroupFeedId) currentGroup = props.groupsDetailed[hasGroupFeedId];
      const fullFunctionality = !!(
        !hasGroupFeedId ||
        (currentGroup && currentGroup.is_join && !currentGroup.is_requested)
      );

      // // Limit amount of posts to render
      let postsToRender = [];
      if (!fullFunctionality && items.length) postsToRender.push(items[0]);
      else postsToRender = items;
      // Block/Pin headers
      let blockHeader,
        pinnedHeader = <div className="my-5" />;
      if (!props.loading && postsToRender.length) {
        blockHeader = <p className="feed-block-title">Latest</p>;
        pinnedHeader = (
          <p className="feed-block-title">
            <img className="feed-pinnned-icon" src={IconPinned} alt="" /> Pinned
          </p>
        );
      } else if (!props.loading) {
        blockHeader = (
          <p className="feed-block-empty">
            Nothing has been posted on {hasGroupFeedId ? 'this group' : 'your organisation'} feed
            yet
          </p>
        );
      }

      const pinnedPosts = hasGroupFeedId
        ? [...postsToRender].filter((post) => post.is_pinned)
        : [...postsToRender].filter((post) => post.isOrgLevelPinned);

      const latestPosts = hasGroupFeedId
        ? [...postsToRender].filter((post) => !post.is_pinned)
        : [...postsToRender].filter((post) => !post.isOrgLevelPinned);

      const pinnedPostsMap = pinnedPosts.map((post) => renderFeedItem(post, fullFunctionality));
      const latestPostsMap = latestPosts.map((post) => renderFeedItem(post, fullFunctionality));
      const { taskCount, inProgressTaskCount, featureFlags } = props;
      // Render all Posts
      return (
        <>
          {fullFunctionality && props.hasAuthToPost && (
            <CreateFeedItem selectedGroupId={hasGroupFeedId} onToggleActive={onToggleCreateFeed} />
          )}
          {featureFlags.EMPLOYEE_ONBOARDING && taskCount && inProgressTaskCount ? (
            <FeedOnboardingItem taskCount={taskCount} inProgressTaskCount={inProgressTaskCount} />
          ) : null}

          {props.banner &&  props.banner.length && <Banner data={props.banner} />}

          {pinnedHeader}
          {pinnedPostsMap}
          {blockHeader}
          {latestPostsMap}

          <FeedLoading isLoading={props.loading} amount={postsToRender.length ? 1 : 3} />
        </>
      );
    } else {
      return <FeedLoading isLoading amount={3} />;
    }
  };

  const classNames = ['feed'];
  if (state.scrollEnabled) classNames.push('scroll');

  let groupHeader = null;
  if (hasGroupFeedId) {
    const currentGroup = props.groupsDetailed[params?.groupId];
    groupHeader = (
      <FeedGroupHeader
        group={currentGroup}
        toggleJoinGroup={props.toggleJoinGroup}
        history={props.history}
      />
    );
  }

  const currentGroup = props.groupsDetailed[hasGroupFeedId];

  return (
    <div className={classNames.join(' ')} ref={scrollBlock} onScroll={_onScroll}>
      {state.showDailyPulse && props.userData && (
        <DailyPulse hide={toggleHideDailyPulse} sendPulse={props.sendUserPulse} userId={props.userData.id} />
      )}
      <Modal isOpen={state.reportSubmitted} centered>
        <ModalHeader
          toggle={() => {
            props.resetPostRequestSuccess();
            setState({ ...state, reportSubmitted: false });
          }}
        >
          <div>Report received</div>
        </ModalHeader>
        <ModalBody>
          <div>
            Thank you for your report. It will be reviewed by an administrator who will take
            appropriate action
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            className={'w-100'}
            onClick={() => {
              props.resetPostRequestSuccess();
              setState({ ...state, reportSubmitted: false });
            }}
            outline
          >
            OKAY
          </Button>
        </ModalFooter>
      </Modal>

      <GridContainer>
        <div ref={contentBlock}>
          <UserListModal />

          {hasGroupFeedId ? (
            <FeedGroupHeader
              group={currentGroup}
              toggleJoinGroup={props.toggleJoinGroup}
              history={props.history}
            />
          ) : null}

          <div className={`feed-content ${hasPostSingleId ? 'feed-content__lower-margin' : ''}`}>
            {hasPostSingleId && (
              <div
                className="feed-content-back-button"
                onClick={() => {
                  handleBack();
                }}
              >
                <FontAwesomeIcon className="me-2" icon={['fas', 'chevron-left']} />
                {BACK_TEXT[props.prevRoute] || 'Go back'}
              </div>
            )}
            {renderContent()}
          </div>
        </div>
      </GridContainer>
    </div>
  );
};

const selectHasAuthToPost = (state, { groupId }) => {
  const groups = state.group.groupsDetailed || {};
  const featureFlags = state.featureFlags || {};
  const isAdmin = state.currentUser.userData && state.currentUser.userData.is_admin ? true : false;

  const group = groups[groupId] || {};
  if (group.is_org_level) return group.is_admin;
  // if (group.is_mandatory) return isAdmin || group.is_moderator
  return isAdmin ? true : featureFlags.CREATE_POST_NORMAL_USER;
};

const Feed = (props) => {
  const state = useSelector((state) => {
    const groupId = props.groupId;
    return {
      ...state,
      singleItem: state.singleItem,
      feed: state.feed,
      events: state.events,
      featureFlags: state.featureFlags,
      taskCount: state.employeeOnboard.tasks.length,
      inProgressTaskCount: state.employeeOnboard.tasks.filter((item) => !item.is_completed).length,
      userImageUrl: state.currentUser.signInData.imageUrl,
      groups: state.group.groups,
      groupsDetailed: state.group.groupsDetailed,
      userData: state.currentUser.userData,
      prevRoute: state.routerHistory.prevPathname,
      myGroups: state.group.myGroups,
      banner: state.banner,
      hasAuthToPost: selectHasAuthToPost(state, { groupId }),
      isAdmin: state.currentUser.userData && state.currentUser.userData.is_admin ? true : false,
    };
  });

  const dispatch = useDispatch();

  const actions = {
    addAlertToAlerts: payload => dispatch(addAlertToAlertsList(payload)),
    getFeed: (payload) => dispatch(getFeedRequest(payload)),
    getFeedItemDetails: (payload) => dispatch(getFeedItemDetailsRequest(payload)),
    getFeedItem: (payload) => dispatch(getFeedItemRequest(payload)),
    resetFeed: () => dispatch(resetFeedPage()),
    resetSingleFeed: () => dispatch(resetSingleFeedPage()),
    postComment: (payload) => dispatch(postCommentRequest(payload)),
    voteOnPost: (payload) => dispatch(voteOnPostRequest(payload)),
    toggleLike: (payload) => dispatch(toggleLikeRequest(payload)),
    toggleCommentLike: (payload) => dispatch(toggleCommentLikeRequest(payload)),
    sendUserPulse: (payload) => dispatch(sendUserPulseRequest(payload)),
    getGroup: (payload) => dispatch(getGroupRequest(payload)),
    toggleJoinGroup: (payload) => dispatch(toggleJoinGroupRequest(payload)),
    populatePostReportTypes: () => dispatch(populatePostReportTypesRequest()),
    resetPostRequestSuccess: () => dispatch(resetPostRequestSuccess()),
    getPasswordMigrationStatus: () => dispatch(getPasswordMigrationStatus()),
    getEmployeeOnboardingTasks: () => dispatch(getTasksRequest()),
    fetchFeatureFlags: () => dispatch(fetchFeatureFlagsRequest()),
    fetchOrgTheme: () => dispatch(getOrgThemeRequest()),
    fetchBanner: () => dispatch(fetchBannerRequest()),
  };

  return <FeedComponent {...props} {...state} {...actions} />;
};

export default Feed;
